import type {
  FindingAction,
  FindingActionKind,
  ValidationIssue,
  ValidationResult,
} from "../types";

// Helpers for the inline-findings validation UI: per-finding action
// lookup, recalculated status (dismissals drop out), the acknowledge gate,
// and the summary-bar counts.

export function findingId(f: ValidationIssue, index: number): string {
  return f.id ?? `${f.type}-${index}`;
}

export function actionMap(
  v: ValidationResult | null | undefined,
): Record<string, FindingActionKind> {
  const m: Record<string, FindingActionKind> = {};
  for (const a of v?.actions ?? []) m[a.finding_id] = a.action;
  return m;
}

// finding id → the user's flag note, for prefilling the note input and
// showing what was flagged. Only flagged actions with a note appear.
export function noteMap(
  v: ValidationResult | null | undefined,
): Record<string, string> {
  const m: Record<string, string> = {};
  for (const a of v?.actions ?? []) {
    if (a.action === "flagged" && a.note) m[a.finding_id] = a.note;
  }
  return m;
}

// Dismissed findings no longer count toward status or display.
export function activeFindings(
  v: ValidationResult,
  actions: Record<string, FindingActionKind>,
): ValidationIssue[] {
  return v.issues.filter((f, i) => actions[findingId(f, i)] !== "dismissed");
}

// Findings whose locator maps to a place in the content (inline marker)
// vs. those that don't (summary bar). Completeness/general → unmappable.
export function isMappable(f: ValidationIssue): boolean {
  return !!f.locator && f.locator.trim().length > 0;
}

// Recalculated overall status after dismissals: any remaining error →
// fail, any remaining warning → warn, else pass.
export function effectiveStatus(
  v: ValidationResult,
  actions: Record<string, FindingActionKind>,
): ValidationResult["status"] {
  const active = activeFindings(v, actions);
  if (active.some((f) => f.severity === "error")) return "fail";
  if (active.some((f) => f.severity === "warning")) return "warn";
  return "pass";
}

// FAIL-level findings still needing review (not dismissed/accepted).
export function unaddressedFailCount(
  v: ValidationResult,
  actions: Record<string, FindingActionKind>,
): number {
  return v.issues.filter((f, i) => {
    if (f.severity !== "error") return false;
    const a = actions[findingId(f, i)];
    return a !== "dismissed" && a !== "accepted";
  }).length;
}

// The acknowledge gate is satisfied when every fail-level finding has been
// individually addressed (dismissed or accepted) — no blanket checkbox.
export function failGateSatisfied(
  v: ValidationResult,
  actions: Record<string, FindingActionKind>,
): boolean {
  return unaddressedFailCount(v, actions) === 0;
}

// Case-level QA sections for the needs-attention review panel (Round
// 10.1, 2B): the stored validation blobs that still gate exports, parsed
// and labeled. Sections with no issues are omitted entirely.
export interface QaSection {
  target: "summary" | "costs";
  label: string;
  validation: ValidationResult;
}

export function qaSections(
  summaryRaw: string | null | undefined,
  costsRaw: string | null | undefined,
): QaSection[] {
  const parse = (raw: string): ValidationResult | null => {
    try {
      const v = JSON.parse(raw) as ValidationResult;
      return Array.isArray(v?.issues) ? v : null;
    } catch {
      return null;
    }
  };
  const out: QaSection[] = [];
  const s = summaryRaw ? parse(summaryRaw) : null;
  if (s && s.issues.length > 0)
    out.push({ target: "summary", label: "Case summary scan", validation: s });
  const c = costsRaw ? parse(costsRaw) : null;
  if (c && c.issues.length > 0)
    out.push({ target: "costs", label: "Costs scan", validation: c });
  return out;
}

// Unaddressed fail-level findings across the QA sections — the number the
// needs-attention line shows.
export function qaUnaddressedCount(sections: QaSection[]): number {
  return sections.reduce(
    (n, s) => n + unaddressedFailCount(s.validation, actionMap(s.validation)),
    0,
  );
}

const TYPE_LABELS: Record<string, string> = {
  chronology: "chronology",
  financial: "financial",
  attribution: "attribution",
  hallucination: "hallucination",
  completeness: "completeness",
  date_grounding: "date",
  amount: "amount",
  citation: "citation",
  uncited_claim: "uncited",
  coverage: "completeness",
};

export function typeLabel(type: string): string {
  return TYPE_LABELS[type] ?? type;
}

// "3 issues: 1 financial, 1 attribution, 1 hallucination · 2 completeness notes"
export function summaryText(
  v: ValidationResult,
  actions: Record<string, FindingActionKind>,
): string {
  const active = activeFindings(v, actions);
  const errors = active.filter((f) => f.severity === "error");
  const warns = active.filter((f) => f.severity === "warning");
  const fmt = (list: ValidationIssue[]) => {
    const counts: Record<string, number> = {};
    for (const f of list) {
      const l = typeLabel(f.type);
      counts[l] = (counts[l] ?? 0) + 1;
    }
    return Object.entries(counts)
      .map(([l, n]) => `${n} ${l}`)
      .join(", ");
  };
  const parts: string[] = [];
  if (errors.length) {
    parts.push(`${errors.length} issue${errors.length === 1 ? "" : "s"}: ${fmt(errors)}`);
  }
  if (warns.length) {
    parts.push(`${warns.length} note${warns.length === 1 ? "" : "s"}: ${fmt(warns)}`);
  }
  return parts.join(" · ");
}

// Apply one action to an actions array (replace existing for that id).
// A note is kept only for "flagged"; switching to dismiss/accept drops it.
export function withAction(
  actions: FindingAction[],
  finding_id: string,
  action: FindingActionKind,
  note?: string,
): FindingAction[] {
  const next: FindingAction = { finding_id, action };
  const trimmed = note?.trim();
  if (action === "flagged" && trimmed) next.note = trimmed;
  return [...actions.filter((a) => a.finding_id !== finding_id), next];
}
